import { AlertTriangle, ArrowRight, Camera, CheckCircle2, ChevronLeft, ChevronRight, ClipboardList, Clock3, Hand, Inbox, ListChecks, QrCode, RefreshCw, Timer } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { normalizeCollection } from '../api/client'
import type { AssetSystem, UserRef, WorkOrder } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { useToast } from '../components/ToastProvider'
import { WorkOrderLink } from '../components/WorkOrderNavigation'
import { Badge, Button, CachedNotice, EmptyState, ErrorState, LoadingState, SearchField } from '../components/ui'
import { useOfflineQuery } from '../hooks/useOfflineQuery'
import { executeOrQueueJson } from '../offline/outbox'
import { useSync } from '../offline/SyncContext'
import { errorMessage, formatDate, formatNumber, workOrderStatusLabel, workOrderStatusTone } from '../utils/format'
import {
  applyQueuedStatus,
  countWorkOrders,
  displayTitle,
  dayKeyOf,
  dayTitle,
  formatDuration,
  groupWorkOrdersByDay,
  isDayKey,
  isMonthKey,
  isForeignWorkOrder,
  isPoolWorkOrder,
  matchesSegment,
  matchesWorkOrderSearch,
  monthKey,
  monthTitle,
  MY_WORK_SEGMENTS,
  nextWorkOrder,
  OWN_OPEN_CACHE_KEY,
  OWN_OPEN_PATH,
  parseSegment,
  queuedStatusOverrides,
  shiftDay,
  shiftMonth,
  sortWorkOrders,
  workOrdersCacheKey,
  workOrdersDayCacheKey,
  workOrdersDayPath,
  workOrdersPath,
  type MyWorkSegment,
  type WorkOrdersScope,
} from './workOrderHelpers'

export function MyWorkPage() {
  const { user, hasAnyRole } = useAuth()
  const sync = useSync()
  const toast = useToast()
  const planner = hasAnyRole('ROLE_PLANNER')
  const [params, setParams] = useSearchParams()
  const [now] = useState(() => new Date())
  // Исполнитель работает по дням: задания выбранного дня (свои и свободные по объекту) плюс все свои открытые задания
  // других дней (просроченные, начатые, возвращённые). Планировщик смотрит месяц и переключает «Мои» / «Все работы».
  const dayMode = !planner
  const monthParam = params.get('month')
  const month = isMonthKey(monthParam) ? monthParam : monthKey(now)
  const dayParam = params.get('day')
  const day = isDayKey(dayParam) ? dayParam : dayKeyOf(now)
  const segment = parseSegment(params.get('view'))
  const scope: WorkOrdersScope = planner && params.get('scope') !== 'all' ? 'mine' : 'all'
  const mine = !planner || scope === 'mine'
  const [search, setSearch] = useState('')
  const [claiming, setClaiming] = useState<string | null>(null)
  const systemId = params.get('systemId') || ''
  const systemless = !systemId && params.get('systemless') === '1'
  const systemFilter = systemId || (systemless ? 'systemless' : '')

  const query = useOfflineQuery<unknown>(dayMode ? workOrdersDayCacheKey(day) : workOrdersCacheKey(month, scope), dayMode ? workOrdersDayPath(day) : workOrdersPath(month, scope), { raw: true, allPages: true, maxAgeMs: 2 * 60_000 })
  const ownQuery = useOfflineQuery<unknown>(OWN_OPEN_CACHE_KEY, OWN_OPEN_PATH, { raw: true, allPages: true, maxAgeMs: 2 * 60_000, enabled: dayMode })
  const systemReferences = useOfflineQuery<AssetSystem[]>('reference:systems', '/reference/systems', { maxAgeMs: 24 * 60 * 60_000 })
  const actor = useMemo<UserRef | null>(() => (user ? { id: user.id, fullName: user.fullName, email: user.email } : null), [user])
  const overrides = useMemo(() => queuedStatusOverrides(sync.queue), [sync.queue])
  const collection = useMemo(() => (query.data ? normalizeCollection<WorkOrder>(query.data) : null), [query.data])
  const ownCollection = useMemo(() => (dayMode && ownQuery.data ? normalizeCollection<WorkOrder>(ownQuery.data) : null), [dayMode, ownQuery.data])
  const periodOrders = useMemo(() => sortWorkOrders((collection?.items ?? []).map((order) => applyQueuedStatus(order, overrides, actor))), [collection, overrides, actor])
  const ownOrders = useMemo(() => sortWorkOrders((ownCollection?.items ?? []).map((order) => applyQueuedStatus(order, overrides, actor))), [ownCollection, overrides, actor])
  // Сводка и список «К выполнению»: задания дня и свои открытые задания других дней, без дублей.
  const orders = useMemo(() => {
    if (!dayMode) return periodOrders
    const seen = new Set(periodOrders.map((order) => order.id))
    return sortWorkOrders([...periodOrders, ...ownOrders.filter((order) => !seen.has(order.id))])
  }, [dayMode, periodOrders, ownOrders])
  const systems = useMemo(() => {
    const objectIds = new Set([...(user?.objects ?? []).map((object) => object.id), ...orders.map((order) => order.object.id)])
    const options = new Map<string, Pick<AssetSystem, 'id' | 'name'>>()
    for (const system of systemReferences.data ?? []) {
      if (planner || objectIds.has(system.objectId ?? '') || system.id === systemId) options.set(system.id, system)
    }
    for (const order of orders) if (order.equipment.system) options.set(order.equipment.system.id, order.equipment.system)
    if (systemId && !options.has(systemId)) options.set(systemId, { id: systemId, name: 'Выбранная система' })
    return [...options.values()].sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  }, [orders, planner, systemId, systemReferences.data, user?.objects])
  const matchesSystem = (order: WorkOrder) => systemless ? !order.equipment.system : !systemId || order.equipment.system?.id === systemId
  const filteredOrders = orders.filter(matchesSystem)
  // Исполнитель видит задания коллег по группе, но его работой они не считаются; планировщику в режиме «Все работы» чужих нет.
  const selfId = planner ? null : user?.id ?? null
  const counters = countWorkOrders(filteredOrders, selfId)
  const next = nextWorkOrder(filteredOrders, selfId)
  const source = dayMode && (segment === 'pool' || segment === 'done' || segment === 'colleagues') ? periodOrders : orders
  const visible = source.filter((order) => matchesSystem(order) && matchesSegment(order, segment, selfId) && matchesWorkOrderSearch(order, search))
  const groups = groupWorkOrdersByDay(visible, now)
  const total = collection?.meta.total ?? periodOrders.length
  const truncated = total > periodOrders.length
  const ownTruncated = dayMode && (ownCollection?.meta.total ?? 0) > ownOrders.length
  const noObjects = !planner && (user?.objects ?? []).length === 0
  const today = dayKeyOf(now)

  function updateParam(key: string, value: string) {
    const nextParams = new URLSearchParams(params)
    if (value) nextParams.set(key, value)
    else nextParams.delete(key)
    setParams(nextParams, { replace: true })
  }

  function chooseSegment(value: MyWorkSegment) {
    updateParam('view', value === 'open' ? '' : value)
  }

  function chooseSystem(value: string) {
    const nextParams = new URLSearchParams(params)
    nextParams.delete('systemId')
    nextParams.delete('systemless')
    if (value === 'systemless') nextParams.set('systemless', '1')
    else if (value) nextParams.set('systemId', value)
    setParams(nextParams, { replace: true })
  }

  async function claim(order: WorkOrder) {
    if (!user) return
    setClaiming(order.id)
    try {
      const result = await executeOrQueueJson<WorkOrder>({ userId: user.id, label: `Взятие в работу ${order.number}`, method: 'POST', path: `/work-orders/${order.id}/claim` })
      if (result.queued) {
        await sync.refreshQueue()
        toast.show(`Задание ${order.number} закреплено за вами на устройстве и отправится при подключении`, 'info')
      } else {
        toast.show(`Задание ${order.number} закреплено за вами. Откройте его и начните работу`, 'success')
        await Promise.all([query.refresh(), dayMode ? ownQuery.refresh() : Promise.resolve()])
      }
    } catch (error) {
      toast.show(errorMessage(error), 'error')
      await query.refresh()
    } finally {
      setClaiming(null)
    }
  }

  const tiles = [
    { key: 'todo', label: 'К выполнению', value: counters.toDo, icon: ClipboardList, segment: 'open' as MyWorkSegment },
    { key: 'progress', label: 'В работе', value: counters.inProgress, icon: RefreshCw, segment: 'in_progress' as MyWorkSegment },
    { key: 'rework', label: 'На доработке', value: counters.rework, icon: AlertTriangle, segment: 'open' as MyWorkSegment },
    { key: 'done', label: 'Выполнено', value: counters.done, icon: CheckCircle2, segment: 'done' as MyWorkSegment },
    ...(!planner || counters.pool > 0 ? [{ key: 'pool', label: 'Свободные на объекте', value: counters.pool, icon: Inbox, segment: 'pool' as MyWorkSegment }] : []),
  ]

  const emptyCopy = systemFilter
    ? { title: 'В этой системе нет заданий по выбранным условиям', description: 'Выберите другой день, состояние или вернитесь ко всем системам.' }
    : search
    ? { title: 'Ничего не найдено', description: 'Измените запрос: ищем по номеру, названию и оборудованию.' }
    : segment === 'open'
      ? { title: dayMode ? 'На этот день заданий к выполнению нет' : 'Заданий к выполнению нет', description: counters.pool > 0 ? (dayMode ? 'Вам ничего не назначено, но на этот день есть свободные задания объекта — возьмите любое в работу.' : 'Вам ничего не назначено, но на объекте есть свободные задания — возьмите любое в работу.') : (dayMode ? 'Посмотрите соседние дни или отсканируйте QR-код оборудования.' : 'Новые задания появятся здесь, как только менеджер запланирует работы.') }
      : segment === 'pool'
        ? { title: dayMode ? 'Свободных заданий на этот день нет' : 'Свободных заданий нет', description: noObjects ? 'Чтобы видеть свободные задания, администратор должен закрепить за вами объект обслуживания.' : !planner && (user?.groups?.length ?? 0) === 0 ? 'Вы не состоите ни в одной группе исполнителей: свободные задания групп вам не показываются. Попросите менеджера добавить вас в группу.' : mine && planner ? 'Свободные задания видны в режиме «Все работы».' : dayMode ? 'Все задания дня распределены. Посмотрите соседние дни.' : 'Все задания ваших объектов распределены. Новые появятся здесь, как только менеджер запланирует работы.' }
        : segment === 'in_progress'
          ? { title: 'Начатых заданий нет', description: 'Откройте задание из списка «К выполнению» и нажмите «Начать работу».' }
          : segment === 'done'
            ? { title: dayMode ? 'Завершённых заданий за этот день нет' : 'Завершённых заданий пока нет', description: dayMode ? 'Здесь появятся выполненные и отменённые работы выбранного дня.' : 'Здесь появятся выполненные и отменённые работы месяца.' }
            : { title: dayMode ? 'Заданий на этот день нет' : 'Заданий на этот месяц нет', description: dayMode ? 'Переключите день стрелками выше.' : 'Переключите месяц стрелками выше.' }

  return (
    <div className="page-stack mywork">
      <section className="page-heading mywork-heading">
        <div>
          <p className="eyebrow">{mine ? 'Исполнение работ' : 'Все работы объекта'}</p>
          <h2>{mine ? 'Мои задания' : 'Задания за месяц'}</h2>
          <p>{dayMode ? 'Задания на день: ваши и свободные по объекту. Просроченные и начатые задания других дней тоже показаны в списке «К выполнению». Свободные задания можно взять в работу.' : mine ? 'Откройте задание, пройдите чек-лист и приложите фото. Свободные задания объекта можно взять в работу — после этого их не увидят другие исполнители.' : 'Все задания объектов за месяц: назначенные, свободные и завершённые.'}</p>
        </div>
        <Link className="button button--secondary mywork-scan" to="/scan"><QrCode size={18} aria-hidden="true" />Сканировать QR</Link>
      </section>

      <section className="mywork-toolbar" aria-label="Период, система и поиск">
        {dayMode ? (
          <div className="month-navigator mywork-month">
            <button type="button" aria-label="Предыдущий день" onClick={() => updateParam('day', shiftDay(day, -1))}><ChevronLeft size={18} aria-hidden="true" /></button>
            <strong>{dayTitle(day, now)}</strong>
            <button type="button" aria-label="Следующий день" onClick={() => updateParam('day', shiftDay(day, 1))}><ChevronRight size={18} aria-hidden="true" /></button>
          </div>
        ) : (
          <div className="month-navigator mywork-month">
            <button type="button" aria-label="Предыдущий месяц" onClick={() => updateParam('month', shiftMonth(month, -1))}><ChevronLeft size={18} aria-hidden="true" /></button>
            <strong>{monthTitle(month)}</strong>
            <button type="button" aria-label="Следующий месяц" onClick={() => updateParam('month', shiftMonth(month, 1))}><ChevronRight size={18} aria-hidden="true" /></button>
          </div>
        )}
        {dayMode && day !== today && <Button type="button" variant="quiet" onClick={() => updateParam('day', '')}>Сегодня</Button>}
        {planner && (
          <div className="segment-control mywork-scope" role="group" aria-label="Чьи задания показывать">
            <button type="button" className={mine ? 'segment-control__active' : ''} aria-pressed={mine} onClick={() => updateParam('scope', '')}>Мои</button>
            <button type="button" className={mine ? '' : 'segment-control__active'} aria-pressed={!mine} onClick={() => updateParam('scope', 'all')}>Все работы</button>
          </div>
        )}
        <label className="field mywork-system"><span>Система</span><select name="workOrderSystem" value={systemFilter} onChange={(event) => chooseSystem(event.target.value)}>
          <option value="">Все системы</option>
          {systems.map((system) => <option key={system.id} value={system.id}>{system.name}</option>)}
          <option value="systemless">Без системы</option>
        </select></label>
        <SearchField value={search} onChange={setSearch} placeholder="Номер, название или оборудование" label="Поиск заданий" />
      </section>

      {query.source === 'cache' && query.fetchedAt && <CachedNotice fetchedAt={query.fetchedAt} stale={query.stale} />}
      {query.error && collection && <div className="form-message form-message--warning" role="status"><AlertTriangle size={18} aria-hidden="true" /><span>{query.error.message}</span></div>}
      {dayMode && ownQuery.error && <div className="form-message form-message--warning" role="status"><AlertTriangle size={18} aria-hidden="true" /><span>Не удалось загрузить ваши открытые задания других дней. Подключитесь к сети и обновите список.</span><Button variant="quiet" onClick={() => void ownQuery.refresh()}>Повторить</Button></div>}
      {ownTruncated && <div className="form-message form-message--warning" role="status"><AlertTriangle size={18} aria-hidden="true" /><span>На устройстве сохранены не все ваши открытые задания. Подключитесь к сети и обновите список.</span></div>}

      {query.loading && <LoadingState label="Загружаем задания" />}
      {query.error && !collection && !query.loading && <ErrorState error={query.error} onRetry={() => void query.refresh()} />}

      {collection && (
        <>
          <NextTask order={next} filtered={Boolean(systemFilter)} />

          <section className="mywork-counters" aria-label="Сводка по заданиям">
            {tiles.map(({ key, label, value, icon: Icon, segment: target }) => (
              <button type="button" className={`mywork-counter mywork-counter--${key}${segment === target && key !== 'rework' ? ' mywork-counter--active' : ''}`} aria-label={label} key={key} onClick={() => chooseSegment(target)}>
                <span className="mywork-counter__icon"><Icon size={19} aria-hidden="true" /></span>
                <strong>{formatNumber(value)}</strong>
                <span>{label}</span>
              </button>
            ))}
          </section>

          <div className="segment-control mywork-segments" role="group" aria-label="Фильтр по состоянию">
            {MY_WORK_SEGMENTS.filter((item) => item.id !== 'colleagues' || (!planner && counters.colleagues > 0)).map((item) => (
              <button type="button" key={item.id} className={segment === item.id ? 'segment-control__active' : ''} aria-pressed={segment === item.id} onClick={() => chooseSegment(item.id)}>{item.label}{item.id === 'pool' && counters.pool > 0 ? ` · ${formatNumber(counters.pool)}` : item.id === 'colleagues' ? ` · ${formatNumber(counters.colleagues)}` : ''}</button>
            ))}
          </div>

          {segment === 'pool' && visible.length > 0 && <div className="form-message form-message--info mywork-pool-note" role="status"><Hand size={18} aria-hidden="true" /><span>Свободные задания видят все исполнители объекта. Нажмите «Взять в работу» — задание закрепится за вами и пропадёт у остальных.</span></div>}
          {truncated && <div className="form-message form-message--warning" role="status"><AlertTriangle size={18} aria-hidden="true" /><span>Показаны первые {formatNumber(periodOrders.length)} из {formatNumber(total)} заданий {dayMode ? 'дня' : 'месяца'}. Уточните поиск или {dayMode ? 'откройте соседний день' : 'выберите другой месяц'}.</span></div>}

          {visible.length === 0
            ? <EmptyState icon={<ListChecks />} title={emptyCopy.title} description={emptyCopy.description} action={systemFilter ? <button type="button" className="button button--secondary" onClick={() => chooseSystem('')}>Показать все системы</button> : segment === 'open' && counters.pool > 0 && !search ? <button type="button" className="button button--secondary" onClick={() => chooseSegment('pool')}>Показать свободные задания</button> : segment !== 'all' && orders.length > 0 && !search ? <button type="button" className="button button--secondary" onClick={() => chooseSegment('all')}>Показать все задания</button> : undefined} />
            : groups.map((group) => (
              <section className="mywork-day" key={group.key} aria-label={group.label}>
                <h3 className="mywork-day__title"><span>{group.label}</span><small>{formatNumber(group.items.length)}</small></h3>
                <div className="task-cards mywork-cards">
                  {group.items.map((order) => <WorkOrderCard order={order} key={order.id} claiming={claiming === order.id} foreign={isForeignWorkOrder(order, selfId)} onClaim={() => void claim(order)} />)}
                </div>
              </section>
            ))}
        </>
      )}
    </div>
  )
}

function NextTask({ order, filtered }: { order: WorkOrder | null; filtered?: boolean }) {
  if (!order) {
    return (
      <section className="mywork-hero mywork-hero--empty">
        <span className="mywork-hero__icon"><CheckCircle2 size={26} aria-hidden="true" /></span>
        <div className="mywork-hero__body">
          <p className="eyebrow">Ближайшее задание</p>
          <h3>{filtered ? 'В выбранной системе открытых заданий нет' : 'Открытых заданий нет'}</h3>
          <p>{filtered ? 'Выберите другой день или систему, чтобы найти следующие задания.' : 'Выберите другой день, возьмите свободное задание объекта или отсканируйте QR-код оборудования.'}</p>
        </div>
      </section>
    )
  }
  const inProgress = order.status === 'IN_PROGRESS'
  const action = inProgress ? 'Продолжить работу' : order.status === 'REJECTED' ? 'Доработать' : 'Открыть задание'
  const code = order.equipment?.externalKey || order.equipment?.code
  return (
    <section className="mywork-hero">
      <div className="mywork-hero__body">
        <p className="eyebrow">{inProgress ? 'Сейчас в работе' : order.status === 'OVERDUE' ? 'Просроченное задание' : order.status === 'REJECTED' ? 'Возвращено на доработку' : 'Ближайшее задание'}</p>
        <h3>{displayTitle(order)}</h3>
        <p>{order.equipment?.name}{code ? ` · ${code}` : ''} · Плановая дата: {formatDate(order.plannedAt)} · {formatDuration(order.durationMinutes)}</p>
        <div className="mywork-hero__meta">
          <Badge tone={workOrderStatusTone(order.status)}>{workOrderStatusLabel(order.status)}</Badge>
          {order.priority === 'HIGH' && <span className="mywork-priority"><AlertTriangle size={14} aria-hidden="true" />Высокий приоритет</span>}
          <span>{order.number}</span>
          {order.startedAt && <span>Начато {formatDate(order.startedAt, true)}</span>}
          {order.checklistCount > 0 && <span>Чек-лист {order.answeredCount}/{order.checklistCount}</span>}
        </div>
      </div>
      <WorkOrderLink className="button button--primary mywork-hero__action" workOrderId={order.id}>{action}<ArrowRight size={17} aria-hidden="true" /></WorkOrderLink>
    </section>
  )
}

function WorkOrderCard({ order, claiming, foreign = false, onClaim }: { order: WorkOrder; claiming: boolean; foreign?: boolean; onClaim(): void }) {
  const code = order.equipment?.externalKey || order.equipment?.code
  const progress = order.checklistCount ? Math.round((order.answeredCount / order.checklistCount) * 100) : 0
  const pool = isPoolWorkOrder(order)
  return (
    <article className={`task-card mywork-card mywork-card--${order.status.toLowerCase()}${pool ? ' mywork-card--pool' : ''}${foreign ? ' mywork-card--foreign' : ''}`}>
      <WorkOrderLink className="mywork-card__link" workOrderId={order.id} aria-label={`${order.number} · ${order.title}`}>
        <div className="task-card__top mywork-card__top">
          <Badge tone={pool ? 'info' : workOrderStatusTone(order.status)}>{pool ? 'Свободное' : workOrderStatusLabel(order.status)}</Badge>
          {pool && order.status === 'OVERDUE' && <Badge tone="danger">Просрочено</Badge>}
          {foreign && order.assignee && <Badge tone="neutral">У коллеги: {order.assignee.fullName}</Badge>}
          {order.priority === 'HIGH' && <span className="task-card__priority mywork-priority"><AlertTriangle size={14} aria-hidden="true" />Высокий приоритет</span>}
          <span className="mywork-card__number">{order.number}</span>
        </div>
        <h3>{displayTitle(order)}</h3>
        <p>{order.equipment?.name || 'Оборудование не указано'}{code ? ` · ${code}` : ''}{pool && order.object?.name ? ` · ${order.object.name}` : ''}</p>
        <div className="mywork-card__facts">
          <span><Clock3 size={15} aria-hidden="true" />{order.startedAt ? `Начато ${formatDate(order.startedAt, true)}` : 'Ещё не начато'}</span>
          <span><Timer size={15} aria-hidden="true" />{formatDuration(order.durationMinutes)}</span>
          {order.maintenanceType && <span>{order.maintenanceType.code || order.maintenanceType.name}</span>}
        </div>
        <div className="task-card__footer mywork-card__footer">
          <span className="mywork-progress" role="img" aria-label={`Чек-лист: ${order.answeredCount} из ${order.checklistCount}`}><i style={{ width: `${progress}%` }} /></span>
          <span className="mywork-card__progress">{order.answeredCount}/{order.checklistCount}</span>
          <span className="mywork-card__photos"><Camera size={15} aria-hidden="true" />{order.photoCount}</span>
        </div>
      </WorkOrderLink>
      {pool && (
        <div className="mywork-card__actions">
          <Button type="button" variant="secondary" loading={claiming} onClick={onClaim} aria-label={`Взять в работу ${order.number}`}><Hand size={16} aria-hidden="true" />Взять в работу</Button>
        </div>
      )}
    </article>
  )
}
